import React, { useEffect, useState } from "react";
import AppCard from "../../../shared/components/AppCard";
import { Button, Table, Modal, Popconfirm, Tooltip, Checkbox, Row, Col, Switch } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  TeamOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import "./scanCenterList.scss";
import ScanCenterForm from "../ScanCenterForm";
import { ScanCenter } from "../../../models/ScanCenter/scanCenter.model";
import ScanCenterService from "../../../services/ScanCenter/scanCenter.service";
import DoctorService from "../../../services/Doctor/doctor.service";
import { PlusOutlined } from "@ant-design/icons";
import AppLoader from "../../../shared/components/AppLoader";
import { generatePath, Link } from "react-router-dom";
import { AppRoutes } from "../../../routes/routeConstants/appRoutes";
import AuthContainer from "../../../store/container/AuthContainer";
import { AuthReducerProps } from "../../../store/reducers/authReducer";
import { AdminTypeEnum } from "../../../enums/AdminType.enum";
import { UserRoleEnum } from "../../../enums/userRole.enum";

interface ScanCenterListProps extends AuthReducerProps {}

function ScanCenterList({ adminType }: ScanCenterListProps) {
  const [scanCenters, setScanCenters] = useState<ScanCenter[]>([]);
  const [scanCenterAdmins, setScanCenterAdmins] = useState<ScanCenter[]>([]);

  const [showScanCenterForm, setShowScanCenterForm] = useState(false);

  const [loading, setLoading] = useState(false);

  const [activeScanCenter, setActiveScanCenter] = useState<ScanCenter>(
    new ScanCenter()
  );

  const [assignableModalOpen, setAssignableModalOpen] = useState(false);
  const [assignableSubject, setAssignableSubject] = useState<ScanCenter | null>(
    null
  );
  const [eligibleDoctors, setEligibleDoctors] = useState<
    Array<{ id: number; doctor_name: string }>
  >([]);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState<number[]>([]);
  const [assignableLoading, setAssignableLoading] = useState(false);

  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);
  const [visibilitySubject, setVisibilitySubject] = useState<ScanCenter | null>(
    null
  );
  const [visibilityDoctors, setVisibilityDoctors] = useState<
    Array<{ id: number; name: string; selected?: boolean }>
  >([]);
  const [selectedVisibilityDoctorIds, setSelectedVisibilityDoctorIds] = useState<
    number[]
  >([]);
  const [visibilityLoading, setVisibilityLoading] = useState(false);

  const [assignmentCapabilityEnabled, setAssignmentCapabilityEnabled] =
    useState(false);
  const [assignmentCapabilityFeatureId, setAssignmentCapabilityFeatureId] =
    useState<number | undefined>(undefined);

  const handleCloseScanCenterForm = () => setShowScanCenterForm(false);

  const openAssignableDoctorsModal = (scanCenter: ScanCenter) => {
    if (!scanCenter.id) return;
    setAssignableSubject(scanCenter);
    setAssignableModalOpen(true);
    setAssignableLoading(true);
    setEligibleDoctors([]);
    setSelectedDoctorIds([]);
    setAssignmentCapabilityEnabled(false);
    setAssignmentCapabilityFeatureId(undefined);

    let featuresDone = false;
    let doctorsDone = false;
    const finishIfBoth = () => {
      if (featuresDone && doctorsDone) {
        setAssignableLoading(false);
      }
    };

    ScanCenterService.fetchScanCenterFeatures(
      scanCenter.id,
      (data) => {
        const eligible = data.eligible_features ?? [];
        const ac = eligible.find((f) => f.name === "assignment_capability");
        setAssignmentCapabilityFeatureId(ac?.id);
        const selected = data.selected_feature_ids ?? [];
        setAssignmentCapabilityEnabled(
          ac?.id !== undefined && selected.includes(ac.id)
        );
      },
      () => {},
      () => {
        featuresDone = true;
        finishIfBoth();
      }
    );

    ScanCenterService.fetchAssignableDoctors(
      scanCenter.id,
      (data) => {
        setEligibleDoctors(data.eligible_doctors ?? []);
        setSelectedDoctorIds(data.selected_doctor_ids ?? []);
      },
      () => {},
      () => {
        doctorsDone = true;
        finishIfBoth();
      }
    );
  };

  const saveAssignableDoctors = () => {
    if (!assignableSubject?.id) return;
    setAssignableLoading(true);
    const featureIds =
      assignmentCapabilityEnabled && assignmentCapabilityFeatureId
        ? [assignmentCapabilityFeatureId]
        : [];
    ScanCenterService.updateScanCenterFeatures(
      assignableSubject.id,
      featureIds,
      () => {
        ScanCenterService.updateAssignableDoctors(
          assignableSubject.id!,
          selectedDoctorIds,
          () => {
            setAssignableModalOpen(false);
          },
          () => {},
          () => {
            setAssignableLoading(false);
          }
        );
      },
      () => {
        setAssignableLoading(false);
      },
      () => {}
    );
  };

  const handleAddScanCenter = () => {
    setActiveScanCenter(new ScanCenter());
    setShowScanCenterForm(true);
  };

  const handleDeleteScanCenter = (scanCenter: ScanCenter) => {
    // console.log(scanCenter);
  };

  const handleScanCenterFormSuccess = (scanCenter: ScanCenter) => {
    setShowScanCenterForm(false);
    const scanCenterIndex = scanCenters.findIndex(
      (currentScanCenter) => scanCenter.id === currentScanCenter.id
    );
    if (scanCenterIndex >= 0) {
      scanCenters[scanCenterIndex] = scanCenter;
    } else {
      scanCenters.unshift(scanCenter);
    }
    setScanCenters([...scanCenters]);
  };

  const openScaVisibilityModal = (sca: ScanCenter) => {
    if (!sca.id) return;
    setVisibilitySubject(sca);
    setVisibilityModalOpen(true);
    setVisibilityLoading(true);
    setVisibilityDoctors([]);
    setSelectedVisibilityDoctorIds([]);
    DoctorService.fetchScanCenterAdminDoctorVisibility(
      sca.id,
      (data: any) => {
        setVisibilityDoctors(data.doctors ?? []);
        setSelectedVisibilityDoctorIds(data.selected_doctor_ids ?? []);
      },
      () => {},
      () => {
        setVisibilityLoading(false);
      }
    );
  };

  const saveScaVisibility = () => {
    if (!visibilitySubject?.id) return;
    setVisibilityLoading(true);
    DoctorService.updateScanCenterAdminDoctorVisibility(
      visibilitySubject.id,
      selectedVisibilityDoctorIds,
      () => {
        setVisibilityModalOpen(false);
      },
      () => {},
      () => {
        setVisibilityLoading(false);
      }
    );
  };

  useEffect(() => {
    setLoading(true);
    ScanCenterService.fetchScanCenters(
      (result: ScanCenter[]) => {
        setScanCenters(
          result.filter((r) => r.type !== AdminTypeEnum.SCAN_CENTER_ADMIN)
        );
        setScanCenterAdmins(
          result.filter((r) => r.type === AdminTypeEnum.SCAN_CENTER_ADMIN)
        );
      },
      () => {},
      () => {
        setLoading(false);
      },
      UserRoleEnum.ADMIN,
      adminType === AdminTypeEnum.TELERAD_ADMIN
    );
  }, [adminType]);

  const columns: any = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
    },
  ];
  const scaColumns: any = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
    },
  ];
  if (adminType === AdminTypeEnum.TELERAD_ADMIN) {
    scaColumns.push({
      title: "Action",
      key: "action",
      width: 120,
      render: (_text: string, sca: ScanCenter) => (
        <Tooltip title="Which of your doctors this scan center admin can assign cases to">
          <EyeOutlined
            onClick={() => openScaVisibilityModal(sca)}
            style={{ fontSize: 18 }}
          />
        </Tooltip>
      ),
    });
  }

  if (adminType === AdminTypeEnum.TELERAD_ADMIN) {
    columns.push({
      title: "Action",
      key: "action",
      render: (text: string, scanCenter: ScanCenter) => {
        return (
          <div className="scan-center-list_actions">
            <Tooltip title="Doctors this scan center can assign cases to">
              <TeamOutlined
                className="mr-3"
                onClick={() => openAssignableDoctorsModal(scanCenter)}
              />
            </Tooltip>
            <EditOutlined
              onClick={() => {
                setShowScanCenterForm(true);
                setActiveScanCenter(scanCenter);
              }}
            />
            {/* Commented for future use -- Krishna */}
            {/* <Popconfirm
              title="Are you sure to delete this Scan center?"
              placement="left"
              onConfirm={() => {
                handleDeleteScanCenter(scanCenter);
              }}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined />
            </Popconfirm> 
            <Tooltip title="View summary">
              <Link
                to={generatePath(AppRoutes.SCAN_CENTER_SUMMARY, {
                  scanCenterId: scanCenter?.id,
                })}
              >
                <FileTextOutlined />
              </Link>
            </Tooltip>*/}
            {/* Commented for future use -- Krishna */}
          </div>
        );
      },
      width: 150,
    });
  }

  return (
    <div className="scan-center-list">
      <AppCard>
        <h2>
          Scan Centers
          {adminType === AdminTypeEnum.TELERAD_ADMIN && (
            <Button
              type="primary"
              className="float-right"
              onClick={handleAddScanCenter}
            >
              <PlusOutlined />
              Add Scan center
            </Button>
          )}
        </h2>
        {loading ? (
          <AppLoader loading={loading} />
        ) : (
          <Table dataSource={scanCenters} columns={columns} rowKey="id" />
        )}
      </AppCard>
      {adminType === AdminTypeEnum.TELERAD_ADMIN && scanCenterAdmins.length > 0 && (
        <AppCard>
          <h2 className="mt-4">Scan center admins</h2>
          <p className="text-muted small mb-3">
            Choose which radiologists each linked scan center admin may assign cases to. Until you
            select at least one doctor, they will not see any doctors from your account.
          </p>
          <Table
            dataSource={scanCenterAdmins}
            columns={scaColumns}
            rowKey="id"
            pagination={false}
          />
        </AppCard>
      )}
      <Modal
        className="primary-modal"
        footer=""
        keyboard={false}
        maskClosable={false}
        destroyOnClose
        visible={showScanCenterForm}
        onCancel={handleCloseScanCenterForm}
      >
        {activeScanCenter && (
          <ScanCenterForm
            onSuccess={handleScanCenterFormSuccess}
            scanCenter={activeScanCenter}
          />
        )}
      </Modal>

      <Modal
        className="primary-modal"
        title="Assignment & whitelisted doctors"
        visible={assignableModalOpen}
        onCancel={() => setAssignableModalOpen(false)}
        onOk={saveAssignableDoctors}
        confirmLoading={assignableLoading}
        okText="Save"
        destroyOnClose
        maskClosable={false}
        width={520}
      >
        {assignableLoading ? (
          <AppLoader loading />
        ) : (
          <>
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between">
                <span>Allow this scan center to assign cases to radiologists</span>
                <Switch
                  checked={assignmentCapabilityEnabled}
                  onChange={setAssignmentCapabilityEnabled}
                  disabled={assignmentCapabilityFeatureId === undefined}
                />
              </div>
              <p className="text-muted small mt-2 mb-0">
                When off, the scan center cannot assign cases or see assigned doctor on listings.
              </p>
            </div>
            {!assignmentCapabilityEnabled ? (
              <p className="text-muted small">
                Enable assignment above to choose which doctors may receive cases.
              </p>
            ) : eligibleDoctors.length === 0 ? (
              <p>No eligible doctors found for this account.</p>
            ) : (
              <Checkbox.Group
                style={{ width: "100%" }}
                value={selectedDoctorIds}
                onChange={(vals) => setSelectedDoctorIds(vals as number[])}
              >
                <Row gutter={[0, 8]}>
                  {eligibleDoctors.map((d) => (
                    <Col span={24} key={d.id}>
                      <Checkbox value={d.id}>{d.doctor_name}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            )}
          </>
        )}
      </Modal>

      <Modal
        className="primary-modal"
        title={
          visibilitySubject
            ? `Doctors visible to ${visibilitySubject.name ?? "scan center admin"}`
            : "Doctor visibility"
        }
        visible={visibilityModalOpen}
        onCancel={() => setVisibilityModalOpen(false)}
        onOk={saveScaVisibility}
        confirmLoading={visibilityLoading}
        okText="Save"
        destroyOnClose
        maskClosable={false}
        width={520}
      >
        {visibilityLoading ? (
          <AppLoader loading />
        ) : visibilityDoctors.length === 0 ? (
          <p className="text-muted">No doctors under your account.</p>
        ) : (
          <Checkbox.Group
            style={{ width: "100%" }}
            value={selectedVisibilityDoctorIds}
            onChange={(vals) => setSelectedVisibilityDoctorIds(vals as number[])}
          >
            <Row gutter={[0, 8]}>
              {visibilityDoctors.map((d) => (
                <Col span={24} key={d.id}>
                  <Checkbox value={d.id}>{d.name}</Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        )}
      </Modal>
    </div>
  );
}

export default AuthContainer(ScanCenterList);
